import { Alert, Modal, Typography } from "antd";
import { useState } from "react";
import {
  ArrowRightOutlined,
  DownOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  diffChangeCount,
  type CollectorDiffEntry,
  type DeviceDiffEntry,
  type FieldChange,
  type SiteConfigDiff,
} from "../utils/siteConfigDiffUtils";

type SiteConfigDiffModalProps = {
  open: boolean;
  diff: SiteConfigDiff;
  onCancel: () => void;
  onOk?: () => void;
  confirmLoading?: boolean;
  mode: "preview" | "save";
  warning?: string;
};

function formatValue(value: unknown): string {
  if (value === undefined) {
    return "—";
  }
  if (value === null) {
    return "null";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value) && !value.some((v) => typeof v === "object")) {
    return value.length === 0 ? "[]" : value.join(", ");
  }
  return JSON.stringify(value);
}

function FieldChangeRow({ change }: { change: FieldChange }) {
  const { field, oldValue, newValue } = change;
  const isAdded = oldValue === undefined;
  const isRemoved = newValue === undefined;
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="font-mono text-gray-500 min-w-[140px]">{field}</span>
      {isAdded ? (
        <span className="text-green-600 font-medium break-all">
          + {formatValue(newValue)}
        </span>
      ) : isRemoved ? (
        <span className="text-red-500 line-through break-all">
          {formatValue(oldValue)}
        </span>
      ) : (
        <span className="flex items-center gap-1.5 flex-wrap">
          <span className="text-red-500 line-through break-all">
            {formatValue(oldValue)}
          </span>
          <ArrowRightOutlined className="text-[10px] text-gray-300" />
          <span className="text-green-600 font-medium break-all">
            {formatValue(newValue)}
          </span>
        </span>
      )}
    </div>
  );
}

function AddedOrRemovedCard({
  name,
  label,
  fields,
  tone,
}: {
  name: string;
  label: string;
  fields: FieldChange[];
  tone: "added" | "removed";
}) {
  const [expanded, setExpanded] = useState(false);
  const isAdded = tone === "added";
  const hasDetails = fields.length > 0;
  const Chevron = expanded ? DownOutlined : RightOutlined;
  return (
    <div
      className={`rounded-lg border overflow-hidden ${
        isAdded ? "border-green-200" : "border-red-200"
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        disabled={!hasDetails}
        className={`flex w-full items-center gap-2 px-3 py-2 border-0 text-left [font:inherit] ${
          isAdded ? "bg-green-50" : "bg-red-50"
        } ${hasDetails ? "cursor-pointer" : ""}`}
      >
        {isAdded ? (
          <PlusCircleOutlined className="text-green-600 text-sm" />
        ) : (
          <MinusCircleOutlined className="text-red-500 text-sm" />
        )}
        <span
          className={`font-semibold text-sm ${
            isAdded ? "text-green-800" : "text-red-800 line-through"
          }`}
        >
          {name}
        </span>
        <span
          className={`ml-auto text-[11px] font-medium px-1.5 py-0.5 rounded ${
            isAdded ? "text-green-600 bg-green-100" : "text-red-600 bg-red-100"
          }`}
        >
          {isAdded ? "New" : "Removed"} {label}
        </span>
        {hasDetails && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            {fields.length} field{fields.length === 1 ? "" : "s"}
            <Chevron className="text-[10px]" />
          </span>
        )}
      </button>
      {hasDetails && expanded && (
        <div
          className={`px-3 py-2 flex flex-col gap-1 border-t ${
            isAdded ? "border-green-200" : "border-red-200"
          }`}
        >
          {fields.map((c) => (
            <FieldChangeRow key={c.field} change={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function ChangedCard({
  name,
  label,
  changes,
}: {
  name: string;
  label: string;
  changes: FieldChange[];
}) {
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
        <EditOutlined className="text-amber-500 text-sm" />
        <span className="font-semibold text-sm text-gray-800">{name}</span>
        <span className="ml-auto text-[11px] font-medium text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
          Modified {label}
        </span>
      </div>
      <div className="px-3 py-2 flex flex-col gap-1">
        {changes.map((c) => (
          <FieldChangeRow key={c.field} change={c} />
        ))}
      </div>
    </div>
  );
}

function DeviceEntry({ entry }: { entry: DeviceDiffEntry }) {
  if (entry.kind === "added") {
    return (
      <AddedOrRemovedCard
        name={entry.name}
        label="device"
        fields={entry.fields}
        tone="added"
      />
    );
  }
  if (entry.kind === "removed") {
    return (
      <AddedOrRemovedCard
        name={entry.name}
        label="device"
        fields={entry.fields}
        tone="removed"
      />
    );
  }
  return (
    <ChangedCard name={entry.name} label="device" changes={entry.changes} />
  );
}

function CollectorEntry({ entry }: { entry: CollectorDiffEntry }) {
  if (entry.kind === "added") {
    return (
      <AddedOrRemovedCard
        name={entry.name}
        label="collector"
        fields={entry.fields}
        tone="added"
      />
    );
  }
  if (entry.kind === "removed") {
    return (
      <AddedOrRemovedCard
        name={entry.name}
        label="collector"
        fields={entry.fields}
        tone="removed"
      />
    );
  }
  return (
    <ChangedCard name={entry.name} label="collector" changes={entry.changes} />
  );
}

function Section<T>({
  title,
  entries,
  render,
}: {
  title: string;
  entries: T[];
  render: (entry: T, idx: number) => React.ReactNode;
}) {
  if (entries.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-2">
      <Typography.Text strong className="text-sm">
        {title} ({entries.length})
      </Typography.Text>
      <div className="flex flex-col gap-2">{entries.map(render)}</div>
    </div>
  );
}

export function SiteConfigDiffModal({
  open,
  diff,
  onCancel,
  onOk,
  confirmLoading,
  mode,
  warning,
}: SiteConfigDiffModalProps) {
  const devicesAdded = diff.devices.filter((d) => d.kind === "added");
  const devicesChanged = diff.devices.filter((d) => d.kind === "changed");
  const devicesRemoved = diff.devices.filter((d) => d.kind === "removed");
  const collectorsAdded = diff.collectors.filter((c) => c.kind === "added");
  const collectorsChanged = diff.collectors.filter((c) => c.kind === "changed");
  const collectorsRemoved = diff.collectors.filter((c) => c.kind === "removed");

  const totalChanges = diffChangeCount(diff);
  const isEmpty = totalChanges === 0;

  return (
    <Modal
      title={mode === "save" ? "Confirm Changes" : "Preview Changes"}
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={confirmLoading}
      okText="Save"
      cancelText={mode === "save" ? "Cancel" : "Close"}
      footer={mode === "preview" ? null : undefined}
      width={680}
    >
      {warning && (
        <Alert type="warning" showIcon message={warning} className="mb-4" />
      )}
      {isEmpty ? (
        <Typography.Text>No changes compared to the live site.</Typography.Text>
      ) : (
        <div className="flex flex-col gap-4">
          <Typography.Text type="secondary" className="text-xs">
            {totalChanges} change{totalChanges === 1 ? "" : "s"} compared to the
            live site
          </Typography.Text>
          {diff.gridConnection.length > 0 && (
            <ChangedCard
              name="Grid connection"
              label="grid connection"
              changes={diff.gridConnection}
            />
          )}
          <Section
            title="Devices added"
            entries={devicesAdded}
            render={(e, i) => <DeviceEntry key={`da-${i}`} entry={e} />}
          />
          <Section
            title="Devices changed"
            entries={devicesChanged}
            render={(e, i) => <DeviceEntry key={`dc-${i}`} entry={e} />}
          />
          <Section
            title="Devices removed"
            entries={devicesRemoved}
            render={(e, i) => <DeviceEntry key={`dr-${i}`} entry={e} />}
          />
          <Section
            title="Collectors added"
            entries={collectorsAdded}
            render={(e, i) => <CollectorEntry key={`ca-${i}`} entry={e} />}
          />
          <Section
            title="Collectors changed"
            entries={collectorsChanged}
            render={(e, i) => <CollectorEntry key={`cc-${i}`} entry={e} />}
          />
          <Section
            title="Collectors removed"
            entries={collectorsRemoved}
            render={(e, i) => <CollectorEntry key={`cr-${i}`} entry={e} />}
          />
        </div>
      )}
    </Modal>
  );
}
